import React, { Component } from "react";
import { connect } from "react-redux";
import UserList from "./userList";
import CreateUser from "./createUser";
import Button from "@mui/material/Button";
import "../../assets/css/settings.css";
import Grid from "@mui/material/Grid";
import {
  TextField,
  Paper,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import CompanyContext from "../../helpers/CompanyContext";
import api from "../../helpers/util";

class UserPage extends Component {
  static contextType = CompanyContext;

  constructor(props) {
    super(props);
    this.state = {
      // Company state
      company: null,
      isEditingCompany: false,
      editedCompany: {},

      // Users state
      isPopupOpen: false,
      isUserDetailsOpen: false,
      users: [],
      companies: [],
      roles: [],
      searchText: "",
      selectedUser: null,
      previousCompanyId: null,
      loading: false,
      error: null,
      successMessage: "",
    };
  }

  fetchData = async () => {
    const { selectedCompany } = this.context;
    this.setState({ loading: true, error: null });

    try {
      // Fetch company details for the selected company
      if (selectedCompany) {
        try {
          const companyRes = await api.get(`/company/${selectedCompany}`);
          console.log("company details:", companyRes.data);
          const companyData = companyRes.data.company || companyRes.data.result || null;
          this.setState({
            company: companyData,
            editedCompany: companyData || {},
          });
        } catch (error) {
          console.error("Error fetching company:", error);
        }
      }

      // Fetch users - filter by selected company if one is selected
      try {
        const usersUrl = selectedCompany
          ? `/users?companyid=${selectedCompany}`
          : `/users`;
        const usersRes = await api.get(usersUrl);
        console.log("users fetched:", usersRes.data);
        const usersList = usersRes.data.users || usersRes.data.result || [];
        this.setState({ users: usersList });
      } catch (error) {
        console.error("Error fetching users:", error);
        this.setState({ error: "Failed to load users" });
      }

      // Fetch all companies (for dropdown/reference)
      try {
        const companiesRes = await api.get("/company/all");
        console.log("companies:", companiesRes.data);
        const companiesList = companiesRes.data.companies || companiesRes.data.result || [];
        this.setState({ companies: companiesList });
      } catch (error) {
        console.error("Error fetching companies:", error);
      }

      // Fetch roles
      try {
        const rolesRes = await api.get("/users/roles");
        console.log("roles:", rolesRes.data);
        const rolesList = rolesRes.data.roles || rolesRes.data.result || [];
        this.setState({ roles: rolesList });
      } catch (error) {
        console.error("Error fetching roles:", error);
      }

    } finally {
      this.setState({ loading: false });
    }
  };

  componentDidMount() {
    this.fetchData();

    const { selectedCompany } = this.context;
    this.setState({ previousCompanyId: selectedCompany });
  }

  componentDidUpdate(prevProps, prevState) {
    const { selectedCompany } = this.context;

    if (selectedCompany !== this.state.previousCompanyId) {
      console.log("🔄 Company changed in User page, refetching data...");
      this.setState({
        previousCompanyId: selectedCompany,
        searchText: "",
        isPopupOpen: false,
        isUserDetailsOpen: false,
        selectedUser: null,
        isEditingCompany: false,
      });
      this.fetchData();
    }
  }

  // Company editing handlers
  handleEditCompany = () => {
    this.setState({
      isEditingCompany: true,
      editedCompany: { ...this.state.company },
    });
  };

  handleCancelEditCompany = () => {
    this.setState({
      isEditingCompany: false,
      editedCompany: { ...this.state.company },
    });
  };

  handleCompanyFieldChange = (field) => (event) => {
    this.setState({
      editedCompany: {
        ...this.state.editedCompany,
        [field]: event.target.value,
      },
    });
  };

  handleSaveCompany = async () => {
    const { editedCompany } = this.state;

    try {
      const response = await api.put(`/company/${editedCompany.id}`, editedCompany);

      const updatedCompany = response.data.company || response.data.result;

      this.setState({
        company: updatedCompany,
        editedCompany: updatedCompany,
        isEditingCompany: false,
        successMessage: "Company details updated successfully",
      });

      setTimeout(() => this.setState({ successMessage: "" }), 3000);
    } catch (error) {
      console.error("Error saving company:", error);
      this.setState({ error: "Failed to update company details" });
      setTimeout(() => this.setState({ error: null }), 5000);
    }
  };

  // User management handlers
  handleOpenPopup = () => {
    this.setState({
      isPopupOpen: true,
      searchText: "",
      isUserDetailsOpen: false,
      selectedUser: null,
    });
  };

  handleClosePopup = () => {
    this.setState({ isPopupOpen: false });
    this.fetchData();
  };

  handleSelectedUser = (selectedUser) => {
    this.setState({
      selectedUser,
      isPopupOpen: false,
      isUserDetailsOpen: true,
    });
  };

  handleCloseSelectedUser = () => {
    this.setState({
      isUserDetailsOpen: false,
    });
    this.fetchData();
  };

  handleSearchChange = (event) => {
    this.setState({ searchText: event.target.value.toLowerCase() });
  };

  handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to deactivate this user?")) {
      return;
    }

    try {
      await api.delete(`/users/${userId}`);

      this.setState({ successMessage: "User deactivated successfully" });
      setTimeout(() => this.setState({ successMessage: "" }), 3000);
      this.fetchData();
    } catch (error) {
      console.error("Error deleting user:", error);
      this.setState({ error: error.response?.data?.message || error.message });
      setTimeout(() => this.setState({ error: null }), 5000);
    }
  };

  render() {
    const {
      company,
      isEditingCompany,
      editedCompany,
      searchText,
      selectedUser,
      users,
      companies,
      roles,
      loading,
      error,
      successMessage,
    } = this.state;

    const { selectedCompany } = this.context;

    return (
      <div className="customer-page">
        <Typography
          variant="h4"
          color="primary"
          align="center"
          gutterBottom
          sx={{ mb: 3 }}
        >
          Client Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Company Details Card */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Company Details</Typography>
            {company && !isEditingCompany ? (
              <Button
                startIcon={<EditIcon />}
                variant="outlined"
                onClick={this.handleEditCompany}
                size="small"
              >
                Edit
              </Button>
            ) : company && isEditingCompany ? (
              <Box>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  color="primary"
                  onClick={this.handleSaveCompany}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  Save
                </Button>
                <Button
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  onClick={this.handleCancelEditCompany}
                  size="small"
                >
                  Cancel
                </Button>
              </Box>
            ) : null}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : company ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={
                    isEditingCompany
                      ? editedCompany.name || ""
                      : company.name || ""
                  }
                  onChange={this.handleCompanyFieldChange("name")}
                  disabled={!isEditingCompany}
                  size="small"
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="ABN"
                  value={
                    isEditingCompany
                      ? editedCompany.abn || ""
                      : company.abn || ""
                  }
                  onChange={this.handleCompanyFieldChange("abn")}
                  disabled={!isEditingCompany}
                  size="small"
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={
                    isEditingCompany
                      ? editedCompany.streetaddress || ""
                      : company.streetaddress || ""
                  }
                  onChange={this.handleCompanyFieldChange("streetaddress")}
                  disabled={!isEditingCompany}
                  size="small"
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={
                    isEditingCompany
                      ? editedCompany.city || ""
                      : company.city || ""
                  }
                  onChange={this.handleCompanyFieldChange("city")}
                  disabled={!isEditingCompany}
                  size="small"
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={
                    isEditingCompany
                      ? editedCompany.stateprovince || ""
                      : company.stateprovince || ""
                  }
                  onChange={this.handleCompanyFieldChange("stateprovince")}
                  disabled={!isEditingCompany}
                  size="small"
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Postcode"
                  value={
                    isEditingCompany
                      ? editedCompany.postalcode || ""
                      : company.postalcode || ""
                  }
                  onChange={this.handleCompanyFieldChange("postalcode")}
                  disabled={!isEditingCompany}
                  size="small"
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={
                    isEditingCompany
                      ? editedCompany.phonenumber || ""
                      : company.phonenumber || ""
                  }
                  onChange={this.handleCompanyFieldChange("phonenumber")}
                  disabled={!isEditingCompany}
                  size="small"
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={
                    isEditingCompany
                      ? editedCompany.emailaddress || ""
                      : company.emailaddress || ""
                  }
                  onChange={this.handleCompanyFieldChange("emailaddress")}
                  disabled={!isEditingCompany}
                  size="small"
                  margin="dense"
                />
              </Grid>
            </Grid>
          ) : (
            <Typography color="text.secondary" align="center">
              {selectedCompany
                ? "Loading company details..."
                : "Select a company from the dropdown above to view details."}
            </Typography>
          )}
        </Paper>

        {/* Users Section */}
        <Paper sx={{ p: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              Users {users.length > 0 && `(${users.length})`}
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <TextField
                size="small"
                placeholder="Search users..."
                value={searchText}
                onChange={this.handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 200 }}
              />
              <Button
                startIcon={<PersonAddIcon />}
                variant="contained"
                color="primary"
                onClick={this.handleOpenPopup}
                size="small"
              >
                Add User
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Create User Popup */}
          {this.state.isPopupOpen && (
            <CreateUser
              handleClose={this.handleClosePopup}
              companies={companies}
              roles={roles}
              defaultCompanyId={selectedCompany}
            />
          )}

          {/* User List */}
          <UserList
            users={users}
            searchText={searchText}
            companies={companies}
            roles={roles}
            onUserSelect={this.handleSelectedUser}
            onUserDelete={this.handleDeleteUser}
            loading={loading}
          />

          {/* Edit User Popup */}
          {this.state.isUserDetailsOpen && selectedUser && (
            <CreateUser
              handleClose={this.handleCloseSelectedUser}
              companies={companies}
              roles={roles}
              userId={selectedUser.id}
            />
          )}
        </Paper>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(UserPage);